// client/src/quotaManager.js
// Unified quota tracking — replaces the old reportsCombo inline logic.
// All quota reads/writes go to subscriptions/{phone}.quota in Firestore.
//
// Each bucket stores { used, bonus, limit } so limits are visible and
// editable directly in Firestore. The system reads `limit` from Firestore
// first; only falls back to planConfig defaults when missing.

import { setDoc } from 'firebase/firestore';
import { auth } from './firebaseConfig';
import { getLimitsForPlan, QUOTA_CYCLE_DAYS, LEGACY_PLAN_MAP } from './planConfig';
import { resolveSubscriptionDoc } from './subscriptionDoc';

const CYCLE_MS = QUOTA_CYCLE_DAYS * 24 * 60 * 60 * 1000;
const KNOWN_PLANS = new Set(['demo', 'basic', 'pro', 'research']);

// Helper: parse Firestore date value to JS Date
function toDate(v) {
  if (!v) return null;
  if (v.toDate && typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  if (typeof v === 'object' && v !== null && typeof v.seconds === 'number')
    return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

function normalizePlanId(plan) {
  return String(plan || '').trim().toLowerCase();
}

function resolvePlanIdFromSubscription(data) {
  const now = new Date();
  const startDate = toDate(data?.startDate);
  const endDate = toDate(data?.endDate);
  const isActive = !!(endDate && endDate.getTime() > now.getTime());

  const rawPlan = normalizePlanId(data?.plan);
  const mappedPlan = LEGACY_PLAN_MAP[rawPlan] || rawPlan;
  if (mappedPlan === 'demo' && isActive) {
    const trialWindowMs = 4 * 24 * 60 * 60 * 1000;
    const looksLikeTrialDuration =
      !!(startDate && endDate && (endDate.getTime() - startDate.getTime()) <= trialWindowMs);
    const isRealTrial = data?.isTrial === true && looksLikeTrialDuration;
    if (!isRealTrial) return 'basic';
  }
  if (KNOWN_PLANS.has(mappedPlan)) return mappedPlan;

  const syncedPlan = normalizePlanId(data?.quota?.syncedPlan);
  if (KNOWN_PLANS.has(syncedPlan)) return syncedPlan;

  // Avoid accidental demo fallback for active users on unknown legacy plan ids.
  return isActive ? 'basic' : 'demo';
}

/**
 * Build a fresh quota block with explicit limits from planConfig.
 * @param {string} planId — e.g. 'basic', 'pro', 'demo'
 */
export function freshQuota(planId) {
  const now = new Date();
  const limits = getLimitsForPlan(planId || 'demo');
  return {
    cycleStart: now,
    cycleEnd: new Date(now.getTime() + CYCLE_MS),
    searches: { used: 0, bonus: 0, limit: limits.searches },
    results: { used: 0, bonus: 0, limit: limits.results },
    tmBids: { used: 0, bonus: 0, limit: limits.tmBids },
    reportRows: { used: 0, bonus: 0, limit: limits.reportRows },
  };
}

/**
 * Ensures quota object exists and cycle is current; auto-resets if expired.
 * Preserves Firestore `limit` values if present (so manual overrides stick).
 * @param {object|null} raw — existing quota from Firestore
 * @param {string} planId — used only when creating fresh / resetting cycle
 */
export function normalizeQuota(raw, planId) {
  const defaults = getLimitsForPlan(planId || 'demo');

  if (!raw) return freshQuota(planId);

  const now = new Date();
  const cycleEnd = toDate(raw.cycleEnd);

  // Cycle expired or missing — reset used and one-time bonus for the new cycle.
  if (!cycleEnd || cycleEnd.getTime() < now.getTime()) {
    return {
      syncedPlan: raw.syncedPlan || undefined,
      cycleStart: now,
      cycleEnd: new Date(now.getTime() + CYCLE_MS),
      searches: { used: 0, bonus: 0, limit: raw.searches?.limit ?? defaults.searches },
      results: { used: 0, bonus: 0, limit: raw.results?.limit ?? defaults.results },
      tmBids: { used: 0, bonus: 0, limit: raw.tmBids?.limit ?? defaults.tmBids },
      reportRows: { used: 0, bonus: 0, limit: raw.reportRows?.limit ?? defaults.reportRows },
    };
  }

  return {
    syncedPlan: raw.syncedPlan || undefined,
    cycleStart: toDate(raw.cycleStart) || now,
    cycleEnd,
    searches: {
      used: raw.searches?.used || 0,
      bonus: raw.searches?.bonus || 0,
      limit: raw.searches?.limit ?? defaults.searches,
    },
    results: {
      used: raw.results?.used || 0,
      bonus: raw.results?.bonus || 0,
      limit: raw.results?.limit ?? defaults.results,
    },
    tmBids: {
      used: raw.tmBids?.used || 0,
      bonus: raw.tmBids?.bonus || 0,
      limit: raw.tmBids?.limit ?? defaults.tmBids,
    },
    reportRows: {
      used: raw.reportRows?.used || 0,
      bonus: raw.reportRows?.bonus || 0,
      limit: raw.reportRows?.limit ?? defaults.reportRows,
    },
  };
}

// Wait for Firebase auth to fully initialize (resolves immediately if already ready).
function waitForAuth() {
  return new Promise((resolve) => {
    if (auth.currentUser) { resolve(auth.currentUser); return; }
    const unsub = auth.onAuthStateChanged((user) => {
      unsub();
      resolve(user);
    });
  });
}

// Read subscription doc from Firestore (null if not found / not logged in).
async function getSubDoc() {
  const user = await waitForAuth();
  if (!user || !user.phoneNumber) return null;
  const { ref, snap, id } = await resolveSubscriptionDoc(user.phoneNumber);
  return snap.exists() ? { ref, data: snap.data(), phone: id } : null;
}

/**
 * consumeQuota('searches' | 'results' | 'tmBids' | 'reportRows', units)
 * Reads limit from Firestore bucket first (so manual overrides work).
 * Returns { ok, remaining, limit, used, reason? }
 */
export async function consumeQuota(quotaType, units = 1) {
  const sub = await getSubDoc();
  if (!sub) return { ok: false, remaining: 0, limit: 0, used: 0, reason: 'Please login and subscribe to continue.' };

  const { ref, data } = sub;
  const now = new Date();

  // Subscription validity is enforced by ProtectedRoute (frontend) and tmQuota.js (backend).
  // Here we only check quota limits. Expired users have limit=0 after backfill.

  const planId = resolvePlanIdFromSubscription(data);
  const quota = normalizeQuota(data.quota, planId);
  const bucket = quota[quotaType] || { used: 0, bonus: 0, limit: 0 };

  // limit comes from Firestore bucket (editable), bonus is additive
  const effectiveLimit = (bucket.limit || 0) + (bucket.bonus || 0);
  const used = bucket.used || 0;
  const remaining = effectiveLimit - used;

  if (units > remaining) {
    return {
      ok: false,
      remaining: Math.max(0, remaining),
      limit: effectiveLimit,
      used,
      reason: `Quota exceeded: ${used}/${effectiveLimit} used this cycle. You can purchase add-ons for more quota.`,
    };
  }

  // Increment usage — preserve syncedPlan so UserContext sync doesn't re-trigger
  const updatedQuota = { ...quota };
  updatedQuota[quotaType] = { ...bucket, used: used + units };
  if (data.quota?.syncedPlan) updatedQuota.syncedPlan = data.quota.syncedPlan;

  await setDoc(ref, { quota: updatedQuota, updatedAt: now }, { merge: true });

  return {
    ok: true,
    remaining: remaining - units,
    limit: effectiveLimit,
    used: used + units,
  };
}

/**
 * getQuotaStatus() — returns all 3 quotas with used/limit/remaining/bonus
 * Reads limit from Firestore (not just planConfig).
 */
export async function getQuotaStatus() {
  const sub = await getSubDoc();
  if (!sub) return null;

  const { data } = sub;
  const planId = resolvePlanIdFromSubscription(data);
  const quota = normalizeQuota(data.quota, planId);

  const result = {
    cycleStart: quota.cycleStart,
    cycleEnd: quota.cycleEnd,
  };

  for (const key of ['searches', 'results', 'tmBids', 'reportRows']) {
    const bucket = quota[key] || { used: 0, bonus: 0, limit: 0 };
    const baseLimit = bucket.limit || 0;
    const bonus = bucket.bonus || 0;
    const effectiveLimit = baseLimit + bonus;
    result[key] = {
      used: bucket.used || 0,
      limit: effectiveLimit,
      baseLimit,
      bonus,
      remaining: Math.max(0, effectiveLimit - (bucket.used || 0)),
    };
  }

  return result;
}

/**
 * addBonus(quotaField, amount) — for add-on purchases.
 * Adds bonus quota for the current cycle.
 */
export async function addBonus(quotaField, amount) {
  const sub = await getSubDoc();
  if (!sub) return false;

  const { ref, data } = sub;
  const now = new Date();
  const planId = resolvePlanIdFromSubscription(data);
  const quota = normalizeQuota(data.quota, planId);
  const bucket = quota[quotaField] || { used: 0, bonus: 0, limit: 0 };
  bucket.bonus = (bucket.bonus || 0) + amount;
  quota[quotaField] = bucket;
  if (data.quota?.syncedPlan) quota.syncedPlan = data.quota.syncedPlan;

  await setDoc(ref, { quota, updatedAt: now }, { merge: true });
  return true;
}
